/* Display formatters for the analytics dashboard.
 *
 * Pure and deterministic (no locale surprises, no Date.now): the API returns
 * raw numbers (USD, bytes, tokens, ms, 0–100 percentages) and the UI formats.
 * Money is the primary metric, so currency formatting is the careful one —
 * it adapts precision to magnitude so sub-cent and three-figure costs both
 * read cleanly. */

/** USD with magnitude-aware precision: $0.0042, $1.27, $48.30, $1.2K, $1.84M. */
export function formatUSD(
  usd: number,
  opts: { compact?: boolean } = {},
): string {
  if (!Number.isFinite(usd)) return "—";

  const sign = usd < 0 ? "-" : "";
  const v = Math.abs(usd);

  if (v === 0) return "$0";

  if (opts.compact && v >= 1000) {
    return `${sign}$${formatCompact(v)}`;
  }

  if (v >= 1000) {
    return `${sign}$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }

  if (v >= 1) return `${sign}$${v.toFixed(2)}`;

  if (v >= 0.01) return `${sign}$${v.toFixed(3)}`;

  // sub-cent: show up to 4 significant-ish decimals without trailing noise.
  return `${sign}$${v.toPrecision(2).replace(/\.?0+$/, "")}`;
}

/** Compact number: 940, 4.0K, 1.2M, 3.4B. One decimal where it adds signal. */
export function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return "—";

  const sign = n < 0 ? "-" : "";
  const v = Math.abs(n);
  if (v < 1000) return `${sign}${Math.round(v)}`;

  const units = [
    { t: 1e12, s: "T" },
    { t: 1e9, s: "B" },
    { t: 1e6, s: "M" },
    { t: 1e3, s: "K" },
  ];
  for (const { t, s } of units) {
    if (v >= t) {
      const scaled = v / t;
      const str = scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(1);
      return `${sign}${str.replace(/\.0$/, "")}${s}`;
    }
  }
  return `${sign}${Math.round(v)}`;
}

/** Thousands-separated integer: 4,045. */
export function formatInt(n: number): string {
  if (!Number.isFinite(n)) return "—";

  return Math.round(n).toLocaleString("en-US");
}

/** Bytes in IEC units: 0 B, 812 B, 4.3 KB, 167 MB, 1.2 GB. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return "—";

  const sign = bytes < 0 ? "-" : "";
  let v = Math.abs(bytes);
  if (v < 1024) return `${sign}${Math.round(v)} B`;

  const units = ["KB", "MB", "GB", "TB", "PB"];
  let i = -1;
  do {
    v /= 1024;
    i += 1;
  } while (v >= 1024 && i < units.length - 1);
  const str = v >= 100 ? v.toFixed(0) : v.toFixed(1);
  return `${sign}${str.replace(/\.0$/, "")} ${units[i]}`;
}

/** Tokens, compacted: 47K, 1.1M, 41M tok. */
export function formatTokens(tokens: number): string {
  return `${formatCompact(tokens)} tok`;
}

/** A 0–100 percentage as the API returns it: 37.6% (clamped formatting). */
export function formatPct(pct: number, digits = 1): string {
  if (!Number.isFinite(pct)) return "—";

  const fixed = pct.toFixed(digits);
  return `${fixed.replace(/\.0+$/, "")}%`;
}

/** A ratio (0–1) as a percentage. */
export function formatRatioPct(ratio: number, digits = 1): string {
  return formatPct(ratio * 100, digits);
}

/** Durations from ms: 8 ms, 520 ms, 6.8 s, 1.2 min. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return "—";

  if (ms < 1000) return `${Math.round(ms)} ms`;

  const s = ms / 1000;
  if (s < 60) return `${s >= 10 ? s.toFixed(0) : s.toFixed(1)} s`;

  const m = s / 60;
  return `${m >= 10 ? m.toFixed(0) : m.toFixed(1)} min`;
}

/** Signed delta for period-over-period: +12.4%, -3.1%, 0%. */
export function formatSignedPct(pct: number, digits = 1): string {
  if (!Number.isFinite(pct)) return "—";

  if (Math.abs(pct) < 0.05) return "0%";

  const sign = pct > 0 ? "+" : "";
  return `${sign}${formatPct(pct, digits)}`;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const pad = (n: number) => String(n).padStart(2, "0");

/** "Jun 3, 09:40" — compact UTC timestamp for freshness / session times. */
export function formatDateTimeUTC(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** "09:40 UTC" — time-of-day only, for the "data through" note. */
export function formatTimeUTC(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/** "Jun 3" — date only, for chart axes and day buckets. */
export function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** A time-range bound for display: "Jun 4, 11:55" when it carries a time,
 *  "Jun 4" for a whole-day (date-only) bound. */
export function formatBound(iso: string): string {
  return iso.includes("T") ? formatDateTimeUTC(iso) : formatDateShort(iso);
}

/** Hour-of-day axis label: "09:00". */
export function formatHourShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  return `${pad(d.getUTCHours())}:00`;
}

/** Coarse relative age from a reference instant: "just now", "14m ago",
 *  "3h ago", "2d ago". Reference is passed in (no Date.now) for testability. */
export function formatRelativeFrom(iso: string, now: Date): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";

  const mins = Math.round((now.getTime() - then) / 60000);
  if (mins < 1) return "just now";

  if (mins < 60) return `${mins}m ago`;

  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

/** Compact session-duration label from start/end ISO: "42m", "3.2h". */
export function formatSpan(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";

  return formatDuration(ms);
}
